import { HStack, useColorMode, VStack } from '@chakra-ui/react'
import { Call, useDispatch, useSelector } from '@missionlabs/api'
import { Body, Button, Textarea, useToast } from '@missionlabs/react'
import { ChangeEvent, FC, MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import {
    addCallNote,
    CallNoteSliceRootState,
    selectCallNote,
    updateCallNote
} from 'shared/slices/callNoteSlice'
import { useCreateCallNoteMutation, useUpdateCallNoteMutation } from 'shared/store'

const MAX_CHARACTERS = 300
const LINE_BREAK_REGEX = /\r\n/g

interface CallNotesProps {
    call: Call
    onSave?: () => void
}

export const CallNotes: FC<CallNotesProps> = ({ call, onSave }) => {
    const { t } = useTranslation()
    const { colorMode } = useColorMode()
    const { toast } = useToast()
    const dispatch = useDispatch()

    const { callTraceID } = call
    const callNote = useSelector((state: CallNoteSliceRootState) =>
        selectCallNote(state, callTraceID)
    )

    const [createCallNote] = useCreateCallNoteMutation()
    const [editCallNote] = useUpdateCallNoteMutation()

    // When the textarea value changes, update the call note in the store.
    const onChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        let note = e.target.value

        // Ensure we use a normalised line-break
        note = note.replace(LINE_BREAK_REGEX, '\n')

        if (!callNote) {
            dispatch(addCallNote({ callTraceID, note }))
            return
        }

        dispatch(updateCallNote({ callTraceID, note, isSaved: note === callNote.savedNote }))
    }

    const handleSubmit = async (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()
        if (!callNote) return
        const content = callNote.note

        if (content === callNote.savedNote) return
        try {
            // If we haven't saved this note to the API yet, it won't have an ID
            // so we save it, then update the store with the new ID.
            if (!callNote.ID) {
                const { ID } = await createCallNote({ content, callTraceID }).unwrap()
                dispatch(updateCallNote({ callTraceID, ID, isSaved: true, savedNote: content }))
            } else {
                await editCallNote({ callNoteID: callNote.ID, content }).unwrap()
                dispatch(updateCallNote({ callTraceID, isSaved: true, savedNote: content }))
            }
            toast({ title: t('callNotes.saveSuccessful'), status: 'success' })
            onSave?.()
        } catch (ex) {
            toast({ title: t('callNotes.saveError'), status: 'error' })
            dispatch(updateCallNote({ callTraceID, isSaved: false }))
        }
    }

    return (
        <VStack w="full" spacing="8px">
            <HStack justifyContent="space-between" w="full">
                <Body size="sm" sx={{ color: `${colorMode}.tones.periwinkle` }}>
                    {t('callNotes.callNotes')}{' '}
                    {callNote?.note && !callNote.isSaved && `(${t('Unsaved')})`}
                </Body>
                <Body size="sm" sx={{ color: `${colorMode}.tones.periwinkle` }}>
                    {callNote?.note?.length || 0}/{MAX_CHARACTERS}
                </Body>
            </HStack>
            <Textarea
                aria-label="call notes"
                value={callNote?.note || ''}
                onChange={onChange}
                placeholder={t('Enter notes')}
                data-testid="call-notes"
                height="177px"
                maxLength={MAX_CHARACTERS}
                variant="dark"
                whiteSpace="pre-wrap"
            />
            <Button
                variant="dialler"
                h="48px"
                w="full"
                isDisabled={!callNote?.note || callNote.isSaved}
                onClick={handleSubmit}
            >
                Update call notes
            </Button>
        </VStack>
    )
}
